import React, { useState, useEffect, useRef } from 'react';
import { Box, Grid, useMediaQuery, useTheme, CircularProgress } from '@mui/material';
import { Image } from 'antd';
import {
    LeftOutlined,
    RightOutlined,
    DownloadOutlined,
    SwapOutlined,
    RotateLeftOutlined,
    RotateRightOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
    UndoOutlined,
    PlayCircleOutlined,
} from '@ant-design/icons';

interface VehicleImage {
    vehicleImageUrl: string;
}

interface VehicleVideo {
    vehicleVideoUrl: string;
}

interface CarImageGalleryProps {
    vehicle: {
        vehicleBrand?: string;
        vehicleImage?: VehicleImage[];
        vehicleVideo?: VehicleVideo[];
    };
    setTabIndex?: (index: number) => void;
    setGalleryExpanded?: (expanded: boolean) => void;
}

type MediaItem = {
    type: 'image' | 'video';
    url: string;
};

const CarImageGallery: React.FC<CarImageGalleryProps> = ({
    vehicle,
    setTabIndex,
    setGalleryExpanded,
}) => {
    const videoList: MediaItem[] = vehicle?.vehicleVideo?.map((v) => ({
        type: 'video' as const,
        url: v.vehicleVideoUrl,
    })) || [];

    const imageList: MediaItem[] = vehicle?.vehicleImage?.map((i) => ({
        type: 'image' as const,
        url: i.vehicleImageUrl,
    })) || [];

    const mediaList: MediaItem[] = [...videoList, ...imageList];
    const defaultImageIndex = mediaList.findIndex((m) => m.type === 'image');
    const [selectedIndex, setSelectedIndex] = useState(defaultImageIndex === -1 ? 0 : defaultImageIndex);
    const [visible, setVisible] = useState(false);
    const [isPlayingVideo, setIsPlayingVideo] = useState(false);
    const [mediaLoading, setMediaLoading] = useState(true);

    const thumbnailContainerRef = useRef<HTMLDivElement>(null);
    const thumbnailRefs = useRef<(HTMLImageElement | null)[]>([]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const selectedMedia = mediaList[selectedIndex];

    const handleDownload = (url: string) => {
        const suffix = url.slice(url.lastIndexOf('.'));
        const filename = Date.now() + suffix;
        fetch(url)
            .then((res) => res.blob())
            .then((blob) => {
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                URL.revokeObjectURL(blobUrl);
                link.remove();
            });
    };

    const iconStyle: React.CSSProperties = {
        color: '#fff',
        fontSize: isMobile ? 16 : 18,
        cursor: 'pointer',
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (visible || mediaList.length === 0) return;
            if (e.key === 'ArrowRight') {
                setSelectedIndex((prev) => (prev + 1) % mediaList.length);
                setIsPlayingVideo(false);
            } else if (e.key === 'ArrowLeft') {
                setSelectedIndex((prev) => (prev - 1 + mediaList.length) % mediaList.length);
                setIsPlayingVideo(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [mediaList, visible]);

    useEffect(() => {
        const currentThumbnail = thumbnailRefs.current[selectedIndex];
        if (currentThumbnail && thumbnailContainerRef.current) {
            currentThumbnail.scrollIntoView({
                behavior: 'smooth',
                inline: 'center',
                block: 'nearest',
            });
        }
    }, [selectedIndex]);

    useEffect(() => {
        setMediaLoading(true);
    }, [selectedIndex]);

    return (
        <Grid size={{ xs: 12, md: 7 }}>
            <Box>
                {/* Main Media Display */}
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        aspectRatio: '16/12',
                        backgroundColor: '#f9f9f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor:
                            selectedMedia.type === 'video' && !isPlayingVideo ? 'pointer' : 'default',
                    }}
                    onClick={() => {
                        if (selectedMedia.type === 'video') {
                            setIsPlayingVideo(true);
                        } else {
                            setVisible(true);
                        }
                    }}
                >
                    {mediaLoading && (
                        <CircularProgress
                            size={48}
                            sx={{ position: 'absolute', zIndex: 10 }}
                        />
                    )}

                    {selectedMedia.type === 'image' ? (
                        <Box
                            component="img"
                            src={selectedMedia.url}
                            alt={vehicle?.vehicleBrand}
                            onLoad={() => setMediaLoading(false)}
                            onError={() => setMediaLoading(false)}
                            loading="lazy" // ✅ this is the lazy load!
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                display: mediaLoading ? 'none' : 'block',
                            }}
                        />
                    ) : isPlayingVideo ? (
                        <Box
                            component="video"
                            src={selectedMedia.url}
                            controls
                            autoPlay
                            onLoadedData={() => setMediaLoading(false)}
                            onError={() => setMediaLoading(false)}
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                display: mediaLoading ? 'none' : 'block',
                            }}
                        />
                    ) : (
                        <>
                            <Box
                                component="video"
                                src={selectedMedia.url}
                                muted
                                playsInline
                                preload="metadata"
                                onLoadedData={() => setMediaLoading(false)}
                                onError={() => setMediaLoading(false)}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    opacity: 0.7,
                                    display: mediaLoading ? 'none' : 'block',
                                }}
                            />
                            <PlayCircleOutlined
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    fontSize: 64,
                                    color: '#fff',
                                    pointerEvents: 'none',
                                }}
                            />
                        </>
                    )}
                </Box>

                {/* Thumbnails */}
                <Box mt={1} sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <Box
                        onClick={() => thumbnailContainerRef.current?.scrollBy({ left: -120, behavior: 'smooth' })}
                        sx={{
                            position: 'absolute',
                            left: 0,
                            zIndex: 2,
                            backgroundColor: 'rgba(255,255,255,0.7)',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            px: 1,
                            cursor: 'pointer',
                        }}
                    >
                        <LeftOutlined />
                    </Box>

                    <Box
                        ref={thumbnailContainerRef}
                        sx={{
                            display: 'flex',
                            gap: 1,
                            overflowX: 'auto',
                            pb: 0.5,
                            pt: 2,
                            px: 5,
                            scrollbarWidth: 'thin',
                            '&::-webkit-scrollbar': { height: 6 },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                borderRadius: 3,
                            },
                        }}
                    >
                        {mediaList.map((item, idx) => (
                            <Box
                                key={idx}
                                component="div"
                                ref={(el) => {
                                    thumbnailRefs.current[idx] = el as HTMLImageElement | null;
                                }}
                                onClick={() => {
                                    setSelectedIndex(idx);
                                    setIsPlayingVideo(false);
                                }}
                                sx={{
                                    width: 118,
                                    height: 88.5,
                                    position: 'relative',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    border: idx === selectedIndex ? '3px solid #1976d2' : '1px solid #ccc',
                                    opacity: idx === selectedIndex ? 1 : 0.4,
                                }}
                            >
                                {item.type === 'image' ? (
                                    <Box
                                        component="img"
                                        src={item.url}
                                        alt={`Media ${idx + 1}`}
                                        loading="lazy" // ✅ this is the lazy load!
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                ) : (
                                    <Box
                                        component="video"
                                        src={item.url}
                                        muted
                                        playsInline
                                        preload="metadata"
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                )}
                                {item.type === 'video' && (
                                    <PlayCircleOutlined
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            fontSize: 24,
                                            color: 'white',
                                            pointerEvents: 'none',
                                        }}
                                    />
                                )}
                            </Box>
                        ))}
                    </Box>

                    <Box
                        onClick={() => thumbnailContainerRef.current?.scrollBy({ left: 120, behavior: 'smooth' })}
                        sx={{
                            position: 'absolute',
                            right: 0,
                            zIndex: 2,
                            backgroundColor: 'rgba(255,255,255,0.7)',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            px: 1,
                            cursor: 'pointer',
                        }}
                    >
                        <RightOutlined />
                    </Box>
                </Box>

                {/* Image Modal (only for image) */}
                {visible && selectedMedia.type === 'image' && (
                    <Image.PreviewGroup
                        preview={{
                            visible,
                            current: selectedIndex - videoList.length,
                            onVisibleChange: (vis) => setVisible(vis),
                            onChange: (index) => setSelectedIndex(index + videoList.length),
                            toolbarRender: (
                                _,
                                {
                                    transform: { scale },
                                    actions: {
                                        onActive,
                                        onFlipY,
                                        onFlipX,
                                        onRotateLeft,
                                        onRotateRight,
                                        onZoomOut,
                                        onZoomIn,
                                        onReset,
                                    },
                                }
                            ) => (
                                <Box
                                    sx={{
                                        position: 'fixed',
                                        top: { xs: 8, md: 24 },
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        backgroundColor: 'rgba(0,0,0,0.7)',
                                        padding: { xs: '4px 8px', md: '6px 10px' },
                                        borderRadius: 6,
                                        zIndex: 1601,
                                        display: 'flex',
                                        gap: { xs: 1, md: 2 },
                                        alignItems: 'center',
                                        whiteSpace: 'nowrap',
                                        overflowX: 'auto',
                                        scrollbarWidth: 'none',
                                        '&::-webkit-scrollbar': { height: 0 },
                                    }}
                                >
                                    <LeftOutlined style={iconStyle} onClick={() => onActive?.(-1)} />
                                    <RightOutlined style={iconStyle} onClick={() => onActive?.(1)} />
                                    <DownloadOutlined
                                        style={iconStyle}
                                        onClick={() => handleDownload(selectedMedia.url)}
                                    />
                                    <SwapOutlined rotate={90} style={iconStyle} onClick={onFlipY} />
                                    <SwapOutlined style={iconStyle} onClick={onFlipX} />
                                    <RotateLeftOutlined style={iconStyle} onClick={onRotateLeft} />
                                    <RotateRightOutlined style={iconStyle} onClick={onRotateRight} />
                                    <ZoomOutOutlined style={iconStyle} onClick={onZoomOut} disabled={scale === 1} />
                                    <ZoomInOutlined style={iconStyle} onClick={onZoomIn} disabled={scale === 50} />
                                    <UndoOutlined style={iconStyle} onClick={onReset} />
                                </Box>
                            ),
                        }}
                    >
                        {imageList.map((img, idx) => (
                            <Image
                                key={idx}
                                src={img.url}
                                alt={`Preview ${idx + 1}`}
                                loading="lazy" // ✅ this is the lazy load!
                                style={{
                                    width: isMobile ? '70vw' : '100%',
                                    maxWidth: isMobile ? '70vw' : '90vw',
                                    maxHeight: isMobile ? '35vh' : '80vh',
                                    objectFit: 'contain',
                                    padding: isMobile ? '6px' : '0',
                                }}
                            />
                        ))}
                    </Image.PreviewGroup>
                )}
            </Box>
        </Grid>
    );
};

export default CarImageGallery;
